import Logger from '../lib/logger';
import StorageService from '../lib/storage.service';
import { deepClone, wholeNumber } from '../lib/util.functions';
import { DEFAULT_BUNDLE_DATA } from '../lib/constants';

/* 
DEFAULT_BUNDLE_DATA = {
  currentStep: 1,
  completedSteps: 0,
  variantId: undefined,
  price: 0,
  quantity: 0,
  title: undefined,
  image: undefined,
  labels: [],
  addedComponentReference: [],
  addedComponentQuantities: [],
  addedComponentPrices: [],
  addedComponentTitles: [],
  addedComponentImages: [],
  addedComponentSteps: [],
  addedComponentOptions: [],
  addedComponentProductIds: [],
  addedComponentMultiple: [],
  addedComponentCustomText: [],
  addedComponentDiscounts: [],
  addedComponentGroupIndex: [],
  addedComponentOnlyForProductIds: []
}
*/

class CustomBundle extends HTMLElement {
  #NAME = 'CustomBundle';
  #storageId = undefined;
  #dataObj = deepClone(DEFAULT_BUNDLE_DATA);

  /* static removeById(id) {
    StorageService.clear(id);
  }

  static getById(id) {
    return StorageService.get(id);
  } */

  get id() {
    return this.#storageId
  }

  get currentStep() {
    return this.#dataObj.currentStep
  }

  set currentStep(num) {
    this.#dataObj.currentStep = num;
    StorageService.save(this.#storageId, this.#dataObj, true);
  }

  get completedSteps() {
    return this.#dataObj.completedSteps
  }

  set completedSteps(num) {
    this.#dataObj.completedSteps = num;
    StorageService.save(this.#storageId, this.#dataObj, true);
  }

  get labels() {
    return this.#dataObj.labels
  }

  set labels(arr) {
    this.#dataObj.labels = arr;
    StorageService.save(this.#storageId, this.#dataObj, true);
  }

  get data() {
    this.#dataObj = StorageService.get(this.#storageId);
    return this.#dataObj
  }

  constructor() {
    super();
    Logger.info(`${this.#NAME} > constructor`);
  }

  connectedCallback() {
    Logger.info(`${this.#NAME} > connectedCallback`);
    this.addEventListener('custombundle:event', (e) => this.#onCustomBundleEvent(e))
  }

  static get observedAttributes() {
    return ['data-id', 'data-labels'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    Logger.info(`${this.#NAME} > attributeChangedCallback > ${name} > ${newValue}`);
    //
    if (name === 'data-id') {
      this.#storageId = newValue;
      const objFromStorage = StorageService.get(this.#storageId);
      if (objFromStorage) {
        this.#dataObj = objFromStorage;
        // update state
        setTimeout(() => {
          this.#dispatchBundleStateEvent(true);
        }, 0);
      } else {
        if (this.parentElement.dataset.inIframe === 'true') {
          this.#dataObj.variantId = '1';
          this.#dataObj.quantity = 1;
          StorageService.save(this.#storageId, this.#dataObj, true);
        } else {
          // redirect to product bundle data does not exist in storage
          window.location.href = `/products/${this.#storageId}`;
        }
      }
    }
    //
    if (name === 'data-labels') {
      this.labels = newValue.split(',');
    }
    
  }

  #dispatchBundleStateEvent(replace = false ) {
    const event = new CustomEvent('bundlestate:update', {
      detail: { replace },
      bubbles: true
    });
    this.dispatchEvent(event);
  }

  #onCustomBundleEvent(e) {
    Logger.info(`${this.#NAME} > #onCustomBundleEvent > ${e.detail.name}`, e.detail.data);
    //
    this[e.detail.name]({ ...e.detail.data });
    this.#dispatchBundleStateEvent(e.detail.replace);
  }

  updateCurrentStep({ step }) {
    Logger.info(`${this.#NAME} > updateCurrentStep > ${step} > ${step < this.currentStep}`);
    if (step < this.currentStep) {
      this.currentStep = step;
      StorageService.save(this.#storageId, this.#dataObj, true);
    }
    return this.#dataObj;
  }

  addAccessory({variantId, quantity, price, title, image, options, productId, multiple, customText, discount, groupIndex, onlyForProductId }) {
    Logger.info(`${this.#NAME} > addAccessory`, {variantId, quantity, price, title, image, options, productId, multiple, customText, discount, groupIndex, onlyForProductId});
    this.#dataObj.addedComponentReference.push(variantId);
    this.#dataObj.addedComponentQuantities.push(wholeNumber(quantity));
    this.#dataObj.addedComponentPrices.push(wholeNumber(price));
    this.#dataObj.addedComponentTitles.push(title);
    this.#dataObj.addedComponentImages.push(image ? `${image.split('&width')[0]}$width=160` : null);
    this.#dataObj.addedComponentSteps.push(this.currentStep);
    this.#dataObj.addedComponentOptions.push(options);
    this.#dataObj.addedComponentProductIds.push(productId);
    this.#dataObj.addedComponentMultiple.push(multiple || false);
    this.#dataObj.addedComponentCustomText.push(customText);
    this.#dataObj.addedComponentDiscounts.push(discount?.map(d => wholeNumber(d)) || null);
    this.#dataObj.addedComponentGroupIndex.push(groupIndex ? groupIndex : undefined);
    this.#dataObj.addedComponentOnlyForProductIds.push(onlyForProductId);
    StorageService.save(this.#storageId, this.#dataObj, true);
    return this.#dataObj;
  }

  updateAccessory({ variantId, quantity, price, bundleIndex, step, groupIndex }) {
    Logger.info(`${this.#NAME} > updateAccessory`, { variantId, quantity, price, bundleIndex, step, groupIndex });
    let index = bundleIndex
    if (index === undefined) {
      const ids = this.#dataObj.addedComponentReference.map((id, i) => [id,i]).filter(arr => arr[0] === variantId);
      const idxArr = ids.find(arr => this.#dataObj.addedComponentSteps[arr[1]] === (step ? step : this.#dataObj.currentStep) && (this.#dataObj.addedComponentGroupIndex[arr[1]] === groupIndex));
      /* console.log(ids, idxArr, groupIndex); */
      index = idxArr ? idxArr[1] : null;
    }
    /* console.log(index); */
    if (price) {
      this.#dataObj.addedComponentPrices[index] = wholeNumber(price);
    }
    /* if (discount) {
      this.#dataObj.addedComponentDiscounts[index] = discount.map(d => wholeNumber(d));
    } */
    if (quantity != undefined) {
      const quant = wholeNumber(quantity);
      if (quant === 0) {
        if (step === 1 && groupIndex === 1) {
          this.#dataObj.currentStep = 1;
          this.#dataObj.addedComponentReference = [];
          this.#dataObj.addedComponentQuantities = [];
          this.#dataObj.addedComponentPrices = [];
          this.#dataObj.addedComponentTitles = [];
          this.#dataObj.addedComponentImages = [];
          this.#dataObj.addedComponentSteps = [];
          this.#dataObj.addedComponentOptions = [];
          this.#dataObj.addedComponentProductIds = [];
          this.#dataObj.addedComponentMultiple = [];
          this.#dataObj.addedComponentCustomText = [];
          this.#dataObj.addedComponentDiscounts = [];
          this.#dataObj.addedComponentGroupIndex = [];
          this.#dataObj.addedComponentOnlyForProductIds = [];
        } else {
          this.#dataObj.addedComponentReference.splice(index, 1);
          this.#dataObj.addedComponentQuantities.splice(index, 1);
          this.#dataObj.addedComponentPrices.splice(index, 1);
          this.#dataObj.addedComponentTitles.splice(index, 1);
          this.#dataObj.addedComponentImages.splice(index, 1);
          this.#dataObj.addedComponentSteps.splice(index, 1);
          this.#dataObj.addedComponentOptions.splice(index, 1);
          this.#dataObj.addedComponentProductIds.splice(index, 1);
          this.#dataObj.addedComponentMultiple.splice(index, 1);
          this.#dataObj.addedComponentCustomText.splice(index, 1);
          this.#dataObj.addedComponentDiscounts.splice(index, 1);
          this.#dataObj.addedComponentGroupIndex.splice(index, 1);
          this.#dataObj.addedComponentOnlyForProductIds.splice(index, 1);
        }
      } else {
        this.#dataObj.addedComponentQuantities[index] = quant;
      }
    }
    StorageService.save(this.#storageId, this.#dataObj, true);
    return this.#dataObj;
  }

  remove() {
    Logger.info(`${this.#NAME} > remove > ${this.#storageId}`);
    StorageService.clear(this.#storageId);
    this.#dataObj = deepClone(DEFAULT_BUNDLE_DATA);
    return this.#dataObj;
  }

  save(data) {
    Logger.info(`${this.#NAME} > save > ${this.#storageId}`);
    this.#dataObj = data;
    StorageService.save(this.#storageId, this.#dataObj, true);
    return this.#dataObj;
  }

  #getDiscount(arr) {
    return Array.from(new Set(arr));
  } 

  addToCartData() {
    const discounts = this.#dataObj.addedComponentDiscounts;
    let quantities = this.#dataObj.addedComponentQuantities.map((v,i) => 
      discounts[i] ? this.#getDiscount(discounts[i]).map(() => !i ? 1 : v - 1) : v).flat();
    let prices = this.#dataObj.addedComponentPrices.map((v,i) => 
      discounts[i] ? this.#getDiscount(discounts[i]) : v).flat();
    let ids = this.#dataObj.addedComponentReference.map((v,i) => 
      discounts[i] ? this.#getDiscount(discounts[i]).map(() => v) : v).flat();
    let customText = this.#dataObj.addedComponentCustomText.map((v,i) => 
      discounts[i] ? this.#getDiscount(discounts[i]).map(() => v) : v).flat();
    //
    this.#dataObj.addedComponentReference.forEach((id, i) => {
      /* console.log(id, id.startsWith('none')); */
      if (id.startsWith('none')) {
        quantities[i] = 'remove';
        prices[i] = 'remove';
        ids[i] = 'remove';
        customText[i] = 'remove';
      }
    });
    //
    quantities = quantities.filter(v => v !== 'remove');
    prices = prices.filter(v => v !== 'remove');
    ids = ids.filter(id => id !== 'remove').map(id => `gid://shopify/ProductVariant/${id}`);
    customText = customText.filter(v => v !== 'remove');
    //
    const data = {
      'id': this.#dataObj.variantId,
      'properties': { 
        '_component_reference': JSON.stringify(ids),
        '_component_quantities': JSON.stringify(quantities),
        '_component_prices': JSON.stringify(prices),
        '_component_custom_text': JSON.stringify(customText)
      },
      'quantity': this.#dataObj.quantity.toString()
    };
    //
    /* this.#dataObj.addedComponentCustomText.forEach((val,i) => {
      if (val) {
        const varId = this.#dataObj.addedComponentReference[i];
        const quantity = this.#dataObj.addedComponentQuantities[i];
        const values = val.split('|');
        values.forEach((value, j) => {
          const lineNum = j + 1;
          data['properties'][`Text ${varId} (${i+1}) x${quantity} Line ${lineNum}`] = value;
        });
      }
    }); */
    //
    return data;
  }

  getTotal() {
    const startingPrice = this.#dataObj.price;
    const arr = this.#dataObj.addedComponentPrices.map((p, i) => {
      if (this.#dataObj.addedComponentDiscounts[i]) {
        return [p, this.#dataObj.addedComponentDiscounts[i][1] * (this.#dataObj.addedComponentQuantities[i] - 1)].reduce((a,b) => a + b, 0);
      } else {
        return p * this.#dataObj.addedComponentQuantities[i];
      }
    });
    return arr.reduce((a,b) => a + b, startingPrice) * this.#dataObj.quantity;
  }

  getNumItems() {
    const reduceBy = this.#dataObj.addedComponentReference.filter(id => id.startsWith('none')).length * this.#dataObj.quantity;
    return (this.#dataObj.addedComponentQuantities.reduce((a,b) => a + b, 0) * this.#dataObj.quantity) - reduceBy;
  }
}

customElements.define('custom-bundle', CustomBundle);